import { lazy, type ComponentType } from 'react';

/** Detect chunk/network load failure (broad patterns for all browsers and CDN issues). */
function isChunkLoadErrorForRetry(error: unknown): boolean {
  if (!error || typeof (error as Error).message !== 'string') return false;
  const msg = ((error as Error).message || '').toLowerCase();
  const name = (error as Error).name || '';
  return (
    msg.includes('loading chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('failed to fetch') ||
    msg.includes('err_aborted') ||
    msg.includes('networkerror') ||
    name === 'ChunkLoadError' ||
    name === 'TypeError' // common for "Failed to fetch..."
  );
}

/** Default retry count for chunk loads. */
const DEFAULT_CHUNK_RETRIES = 5;
/** Base delay (ms) for exponential backoff. */
const DEFAULT_RETRY_INTERVAL = 1000;
/** Max delay (ms) between retries. */
const MAX_RETRY_DELAY = 8000;

/**
 * Enhanced lazy loading with retry mechanism for chunk loading failures.
 * Uses exponential backoff and treats network/chunk errors as retryable.
 */
export function lazyWithRetry(
  importFunction: () => Promise<{ default: ComponentType }>,
  retries = DEFAULT_CHUNK_RETRIES,
  interval = DEFAULT_RETRY_INTERVAL
) {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const retry = (attempt: number) => {
        importFunction()
          .then(resolve)
          .catch((error: unknown) => {
            console.warn(`[Chunk] Import failed (attempt ${attempt}/${retries}):`, (error as Error)?.message ?? error);
            const isChunkError = isChunkLoadErrorForRetry(error);

            if (isChunkError && attempt < retries) {
              const delay = Math.min(interval * Math.pow(2, attempt - 1), MAX_RETRY_DELAY);
              console.log(`[Chunk] Retrying in ${delay}ms (${attempt + 1}/${retries})`);
              setTimeout(() => retry(attempt + 1), delay);
            } else {
              console.error('[Chunk] Import failed after retries:', error);
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          });
      };

      retry(1);
    });
  });
}

/**
 * Preload a dynamic import to improve performance
 */
export function preloadRoute(importFunction: () => Promise<unknown>) {
  if (typeof window !== 'undefined') {
    // Use requestIdleCallback if available, otherwise use setTimeout
    const preload = () => {
      importFunction().catch((error) => {
        console.warn('Route preload failed:', error);
      });
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload);
    } else {
      setTimeout(preload, 0);
    }
  }
}

let propertyListingPreload: Promise<unknown> | null = null;

/** Load `/properties` route chunk after hover/focus intent (avoids critical-chain bloat on LCP). */
export function prefetchPropertyListingRoute(): void {
  if (propertyListingPreload != null) return;
  propertyListingPreload = import('../pages/PropertyListingPage').catch((err) => {
    propertyListingPreload = null;
    console.warn('[Prefetch] Property listing chunk failed:', err);
  });
}

/**
 * Clear chunk cache and force reload
 */
export function clearChunkCache() {
  return new Promise((resolve) => {
    if ('caches' in window) {
      caches.keys().then(names => {
        Promise.all(
          names.map(name => caches.delete(name))
        ).then(() => {
          console.log('🧹 Chunk cache cleared');
          resolve();
        });
      });
    } else {
      console.log('🧹 Cache API not available');
      resolve();
    }
  });
}

/**
 * Check if current error is a chunk/network loading error (aligned with retry logic).
 */
export function isChunkLoadError(error: unknown): boolean {
  return isChunkLoadErrorForRetry(error);
}

const CHUNK_AUTO_RETRY_KEY = 'chunk_auto_retry_attempted';

/**
 * Global error handler for unhandled chunk loading errors.
 * First occurrence: automatically clear caches and reload once.
 * If the error persists after reload, show confirm dialog.
 */
export function setupGlobalChunkErrorHandler() {
  window.addEventListener('unhandledrejection', (event) => {
    if (!event.reason || !isChunkLoadError(event.reason)) return;

    event.preventDefault();
    console.warn('[Chunk] Unhandled chunk load error, attempting recovery:', (event.reason as Error)?.message);

    const alreadyRetried = sessionStorage.getItem(CHUNK_AUTO_RETRY_KEY) === '1';

    if (!alreadyRetried) {
      sessionStorage.setItem(CHUNK_AUTO_RETRY_KEY, '1');
      clearChunkCache().then(() => {
        window.location.reload();
      });
      return;
    }

    sessionStorage.removeItem(CHUNK_AUTO_RETRY_KEY);
    const shouldReload = confirm(
      'The page failed to load properly (often after an update). Refresh to get the latest version?'
    );
    if (shouldReload) {
      clearChunkCache().then(() => {
        window.location.reload();
      });
    }
  });

  window.addEventListener('error', (event) => {
    const error = event.error;
    if (error && isChunkLoadError(error)) {
      console.warn('[Chunk] Global chunk error:', (error as Error)?.message);
    }
  });
}